import React, { useState } from 'react';
import * as Select from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface FilterOption {
  value: string;
  label: string;
  color?: string;
}

interface FilterSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: FilterOption[];
  placeholder: string;
  label?: string;
  searchable?: boolean;
  scrollable?: boolean;
  icon?: any;
  width?: string;
  triggerColor?: string;
}

export const FilterSelect: React.FC<FilterSelectProps> = ({
  value,
  onValueChange,
  options,
  placeholder,
  label,
  searchable = false,
  scrollable = false,
  icon,
  width,
  triggerColor
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // If searchable is enabled, filter the options based on the search query.
  const filteredOptions = searchable
    ? options.filter(option =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options;

  return (
    <div className="relative">
      <Select.Root value={value} onValueChange={onValueChange}>
        <Select.Trigger
          className={`flex items-center justify-between ${width ? width : 'w-full'} px-4 py-2 text-sm ${triggerColor || "bg-[#1A1F2E] text-gray-300 border-gray-800"} border rounded-lg hover:bg-[#1A1F2E]/80 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 group`}
        >
          {icon && (
            <>
              {icon}
            </>
          )}
          <Select.Value placeholder={placeholder} />
          <Select.Icon>
            <ChevronDown className="h-4 w-4 text-gray-500 group-hover:text-gray-400 transition-colors" />
          </Select.Icon>
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            className="bg-[#1A1F2E] border border-gray-800 rounded-lg shadow-xl animate-in fade-in-0 zoom-in-95"
            position="popper"
            sideOffset={8}
          >
            {/* Conditionally render the search input */}
            {searchable && (
              <div className="p-2">
                <input 
                  type="text" 
                  placeholder="Search..."
                  autoFocus
                  value={searchQuery} 
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onBlur={(e) => e.currentTarget.focus()}
                  className="w-full px-3 py-2 mb-2 bg-[#1A1F2E] border border-gray-700 rounded text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
            <Select.ScrollUpButton />
            {/* Conditionally add scrollable classes if scrollable is true */}
            <Select.Viewport className={cn("p-1", scrollable ? "max-h-60 overflow-auto" : "")}>
              {filteredOptions.map((option) => (
                <Select.Item
                  key={option.value}
                  value={option.value}
                  className={cn(
                    "flex items-center justify-between px-3 py-2 text-sm rounded-md cursor-pointer outline-none",
                    option.color || "text-gray-300 hover:bg-gray-800/50",
                    "data-[highlighted]:bg-gray-800/50"
                  )}
                >
                  <Select.ItemText>{option.label}</Select.ItemText>
                  <Select.ItemIndicator>
                    <Check className="h-4 w-4" />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.Viewport>
            <Select.ScrollDownButton />
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </div>
  );
};
